import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { vec3 } from "gl-matrix";

import { type RootState } from "app";

export enum LocationStatus {
    Initial,
    Idle,
    Loading,
    Error,
}

interface MyLocationState {
    showMarker: boolean;
    autocenter: boolean;
    currentLocation?: {
        pos: vec3;
        outOfBounds: boolean;
    };
    usedGoToMyLocation: boolean;
    geolocationPositionCoords?: {
        accuracy: number;
        altitude: number | null;
        longitude: number;
        latitude: number;
    };
    status:
        | { status: LocationStatus.Initial | LocationStatus.Idle | LocationStatus.Loading }
        | { status: LocationStatus.Error; msg: string };
}

const initialState: MyLocationState = {
    showMarker: false,
    autocenter: false,
    usedGoToMyLocation: false,
    status: { status: LocationStatus.Initial },
};

type State = typeof initialState;

export const myLocationSlice = createSlice({
    name: "area",
    initialState: initialState,
    reducers: {
        setCurrentLocation: (state, action: PayloadAction<State["currentLocation"]>) => {
            state.currentLocation = action.payload;
        },
        toggleShowMarker: (state, action: PayloadAction<boolean | undefined>) => {
            state.showMarker = action.payload ?? !state.showMarker;
        },
        toggleAutocenter: (state, action: PayloadAction<boolean | undefined>) => {
            state.autocenter = action.payload ?? !state.autocenter;
        },
        setStatus: (state, action: PayloadAction<State["status"]>) => {
            state.status = action.payload;
        },
        setGeolocationPositionCoords: (state, action: PayloadAction<State["geolocationPositionCoords"]>) => {
            state.geolocationPositionCoords = action.payload;
        },
        setUsedGoToMyLocation: (state, action: PayloadAction<boolean>) => {
            state.usedGoToMyLocation = action.payload;
        },
    },
});

export const selectCurrentLocation = (state: RootState) => state.myLocation.currentLocation;
export const selectShowLocationMarker = (state: RootState) => state.myLocation.showMarker;
export const selectMyLocationAutocenter = (state: RootState) => state.myLocation.autocenter;
export const selectLocationStatus = (state: RootState) => state.myLocation.status;
export const selectGeolocationPositionCoords = (state: RootState) => state.myLocation.geolocationPositionCoords;
export const selectUsedGoToMyLocation = (state: RootState) => state.myLocation.usedGoToMyLocation;

const { actions, reducer } = myLocationSlice;
export { actions as myLocationActions, reducer as myLocationReducer };
